import type { PremadeTemplate } from "@/lib/premade-contract-templates";

// Private Investigation proposal template names
export const PI_PROPOSAL_TEMPLATES: string[] = [];

// ──────────────────────────────────────────────────────
//  Legally accurate PI contract templates
// ──────────────────────────────────────────────────────

export const PI_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  /* ───────── 1. General Investigation Retainer Agreement ───────── */
  {
    name: "Investigation Retainer Agreement",
    description:
      "Comprehensive retainer-based agreement for private investigation services — covers scope, confidentiality, evidence handling, legal compliance, and fee structure.",
    content: `PRIVATE INVESTIGATION RETAINER AGREEMENT

This Private Investigation Retainer Agreement ("Agreement") is entered into as of {{date}} by and between:

INVESTIGATOR / AGENCY:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Investigator")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. ENGAGEMENT AND SCOPE OF INVESTIGATION

The Client hereby retains the Investigator to perform professional investigative services as described below. The Investigator shall exercise independent professional judgment in determining the methods and means of investigation, subject to applicable law.

Investigation Type / Case Description:
{{project_description}}

Specific Services and Objectives:
{{scope_of_work}}

The Investigator reserves the right to decline or terminate any assignment that, in the Investigator's sole professional judgment, would require unlawful conduct or would compromise ethical standards.

──────────────────────────────────────────────────

2. TERM AND DURATION

{{contract_length}}

This Agreement shall commence on the date of execution and shall remain in effect until the investigation is concluded, the retainer is exhausted, or the Agreement is terminated by either party in accordance with Section 10 herein.

──────────────────────────────────────────────────

3. COMPENSATION AND FEE STRUCTURE

A. Retainer Deposit
A non-refundable retainer deposit is required prior to the commencement of investigative services. This retainer shall be applied against fees and expenses as incurred.

Estimated Investigation Cost: \${{estimated_cost}}

B. Hourly Rates
All investigative work shall be billed at the following rates:
• Field Investigation / Surveillance: As set forth in the attached fee schedule
• Research, Analysis, and Report Preparation: As set forth in the attached fee schedule
• Court Testimony and Legal Proceedings: As set forth in the attached fee schedule
• Travel Time: Billed at the applicable hourly rate

C. Expenses
The following expenses shall be billed at actual cost plus a 15% administrative surcharge:
• Mileage at the current IRS standard rate
• Database and public records search fees
• Lodging, meals, and per diem (for assignments exceeding 50 miles)
• Specialized surveillance equipment rental
• Court filing fees, subpoena service, and process fees
• Photographic, video, and digital evidence processing
• Expert consultation fees (with prior Client approval)

D. Payment Terms
{{payment_terms}}

Invoices are due upon receipt unless otherwise specified. A late-payment charge of 1.5% per month (18% per annum) shall accrue on all balances past due. The Investigator reserves the right to suspend services if payment is not received within fifteen (15) days of the invoice date.

E. Minimum Billing Increment
All time shall be billed in minimum increments of fifteen (15) minutes.

──────────────────────────────────────────────────

4. CONFIDENTIALITY AND PRIVILEGE

A. The Investigator shall treat all information received from or on behalf of the Client as strictly confidential. No information relating to the Client's identity, the subject of the investigation, or the findings shall be disclosed to any third party without the prior written consent of the Client, except as required by law or court order.

B. If this investigation is conducted at the direction of or in coordination with the Client's legal counsel, the work product may be protected under the attorney-client privilege and/or the work product doctrine. The Client acknowledges that voluntary disclosure of investigative findings to third parties may constitute a waiver of such privilege.

C. The Investigator shall maintain all case files, reports, and evidence in secure storage for a period of not less than three (3) years following the conclusion of the engagement, unless otherwise directed in writing by the Client.

──────────────────────────────────────────────────

5. INVESTIGATIVE METHODS AND LEGAL COMPLIANCE

A. All investigative activities shall be conducted in strict compliance with all applicable federal, state, and local laws, including but not limited to:
• State private investigator licensing statutes
• Federal and state privacy laws (including the Electronic Communications Privacy Act, 18 U.S.C. §§ 2510–2522)
• Fair Credit Reporting Act (FCRA), 15 U.S.C. § 1681 et seq. (where applicable)
• State anti-stalking and harassment statutes
• Trespass and wiretapping laws

B. The Investigator may employ the following lawful investigative techniques as appropriate:
• Physical and mobile surveillance
• Open-Source Intelligence (OSINT) collection
• Database and public records research
• Witness and source interviews
• Forensic document examination
• Social media analysis and digital footprint review
• Photography and video documentation
• Skip tracing and locate services
• Background and due diligence investigations

C. The Investigator shall NOT engage in:
• Unauthorized wiretapping, eavesdropping, or electronic surveillance
• Trespass upon private property
• Impersonation of law enforcement officers or government officials
• Accessing sealed, expunged, or legally restricted records
• Any activity that would constitute a criminal offense under applicable law

──────────────────────────────────────────────────

6. EVIDENCE HANDLING AND CHAIN OF CUSTODY

A. All physical and digital evidence obtained during the investigation shall be handled in accordance with recognized chain-of-custody protocols to preserve its admissibility in legal proceedings.

B. The Investigator shall maintain detailed logs documenting the collection, handling, storage, and transfer of all evidence.

C. Original evidence shall not be altered, modified, or destroyed. Working copies shall be created for analysis purposes.

──────────────────────────────────────────────────

7. REPORTING AND DELIVERABLES

A. The Investigator shall provide the Client with written investigative reports at intervals mutually agreed upon, and a comprehensive final report upon conclusion of the investigation.

B. Reports shall include:
• Summary of investigative activities performed
• Factual findings supported by evidence
• Itemized log of hours and expenses
• Copies of relevant photographs, video recordings, and documents
• Professional opinions and recommendations (where requested)

C. All reports shall contain only factual, objective information. The Investigator does not render legal opinions or legal advice.

──────────────────────────────────────────────────

8. CLIENT OBLIGATIONS AND REPRESENTATIONS

A. The Client represents and warrants that:
• The Client has a lawful purpose for requesting the investigation
• All information provided to the Investigator is true and accurate to the best of the Client's knowledge
• The Client is not requesting services for the purpose of harassment, intimidation, or any unlawful activity

B. The Client agrees to:
• Cooperate fully with the Investigator and provide timely access to relevant information
• Notify the Investigator promptly of any changes in circumstances affecting the investigation
• Not independently contact investigation subjects in a manner that could compromise the investigation

──────────────────────────────────────────────────

9. LIMITATION OF LIABILITY AND DISCLAIMER

A. The Investigator makes no guarantees regarding the outcome, results, or findings of any investigation. Investigations are inherently unpredictable, and results cannot be assured.

B. The Investigator's total aggregate liability under this Agreement shall not exceed the total fees actually paid by the Client for the engagement giving rise to the claim.

C. In no event shall the Investigator be liable for indirect, incidental, consequential, special, or punitive damages, including but not limited to lost profits, emotional distress, or loss of business opportunity, regardless of the theory of liability.

D. The Client agrees to indemnify and hold harmless the Investigator from any claims, damages, or expenses arising from the Client's misuse of investigative findings or the Client's provision of false or misleading information.

──────────────────────────────────────────────────

10. TERMINATION

A. Either party may terminate this Agreement at any time upon written notice to the other party.

B. Upon termination by the Client, the Client shall be responsible for payment of all fees and expenses incurred through the date of termination.

C. The Investigator may terminate immediately if:
• The Client requests unlawful activities
• The Client fails to make timely payment
• Continued investigation poses a risk to the safety of the Investigator or others
• The Client provides materially false information

D. Sections 4 (Confidentiality), 6 (Evidence Handling), and 9 (Limitation of Liability) shall survive termination of this Agreement.

──────────────────────────────────────────────────

11. GENERAL PROVISIONS

A. Governing Law: This Agreement shall be governed by the laws of the state in which the Investigator is licensed.

B. Dispute Resolution: Any disputes arising under this Agreement shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, unless otherwise required by law.

C. Entire Agreement: This Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations, representations, and agreements.

D. Amendment: This Agreement may not be amended except by a written instrument signed by both parties.

E. Severability: If any provision of this Agreement is found to be unenforceable, the remaining provisions shall continue in full force and effect.

F. Assignment: Neither party may assign this Agreement without the prior written consent of the other party.

──────────────────────────────────────────────────

12. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

13. SIGNATURES

By signing below, the parties acknowledge that they have read, understood, and agree to be bound by this Agreement. The Client acknowledges that no guarantees regarding results have been made.

INVESTIGATOR / AGENCY:
Name: {{company_name}}
License No.: ___________________________
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },

  /* ───────── 2. Surveillance Services Agreement ───────── */
  {
    name: "Surveillance Services Agreement",
    description:
      "Specialized contract for surveillance operations — covers surveillance methods, evidence documentation, legal boundaries, and reporting protocols.",
    content: `SURVEILLANCE SERVICES AGREEMENT

This Surveillance Services Agreement ("Agreement") is entered into as of {{date}} by and between:

INVESTIGATOR / AGENCY:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Investigator")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. PURPOSE AND SCOPE

The Client retains the Investigator to conduct lawful surveillance operations as described below:

{{project_description}}

Surveillance Objectives:
{{scope_of_work}}

──────────────────────────────────────────────────

2. SURVEILLANCE OPERATIONS

A. Authorized Methods
The Investigator is authorized to employ the following surveillance techniques:
• Stationary observation from public vantage points
• Mobile surveillance (vehicular and on foot)
• Photographic and video documentation from lawful positions
• GPS tracking (only where expressly permitted by applicable state law and with proper legal authority)
• License plate recognition and vehicle identification
• Activity and pattern-of-life documentation

B. Operational Limitations
The Investigator shall NOT:
• Conduct surveillance from private property without authorization
• Utilize electronic eavesdropping or wiretapping devices
• Engage in activities constituting stalking or harassment under applicable law
• Enter any structure without lawful authority
• Interfere with the subject's lawful activities
• Engage in high-speed vehicular pursuit

C. Subject Safety Protocol
If at any time the Investigator determines that continued surveillance poses a risk to public safety, the safety of the subject, or the Investigator's safety, the Investigator shall immediately discontinue the surveillance and notify the Client.

──────────────────────────────────────────────────

3. EVIDENCE AND DOCUMENTATION

A. All photographic, video, and written documentation shall be time-stamped, geo-tagged where possible, and maintained in accordance with chain-of-custody protocols.

B. The Investigator shall maintain a detailed surveillance log including:
• Date, time, and location of all observations
• Description of subject activity observed
• Weather and visibility conditions
• Equipment used
• Identification of all persons observed with the subject (where determinable)

C. Raw footage and photographs shall be preserved in their original, unaltered format.

──────────────────────────────────────────────────

4. COMPENSATION

Estimated Total Cost: \${{estimated_cost}}
Estimated Hours: {{estimated_hours}}
Payment Terms: {{payment_terms}}

Surveillance operations require a minimum billing of four (4) hours per deployment. Standby time, travel time, and preparation time are billed at the applicable hourly rate.

Additional expenses:
• Mileage at the current IRS standard rate
• Tolls, parking, and related travel expenses at actual cost
• Equipment rental (specialized optics, cameras) at actual cost
• Overnight lodging and per diem for extended operations

──────────────────────────────────────────────────

5. REPORTING

A. The Investigator shall provide:
• Daily activity summaries during active surveillance periods
• Comprehensive written surveillance reports upon conclusion
• Selected photographic and video evidence with annotations
• Expert declarations or affidavits for legal proceedings (upon request, at additional cost)

B. If no relevant activity is observed during a surveillance session, the Investigator shall report the negative results with the same level of documentation.

──────────────────────────────────────────────────

6. CONFIDENTIALITY

All aspects of the surveillance operation — including the Client's identity, the subject's identity, the locations monitored, and the findings — shall remain strictly confidential. The Investigator shall not disclose any information to third parties except as authorized by the Client in writing or as required by law.

──────────────────────────────────────────────────

7. LIABILITY AND INDEMNIFICATION

A. The Investigator shall maintain professional liability (errors and omissions) insurance with minimum coverage of $1,000,000.

B. The Client acknowledges that surveillance results cannot be guaranteed, and that the subject's activities may not yield the information sought.

C. The Client agrees to indemnify and hold harmless the Investigator from any claims arising from the Client's unauthorized use or distribution of surveillance evidence.

D. The Investigator's total liability shall not exceed the fees paid under this Agreement.

──────────────────────────────────────────────────

8. TERM AND TERMINATION

{{contract_length}}

Either party may terminate this Agreement upon written notice. The Client shall be responsible for all fees and expenses incurred through the date of termination. The confidentiality and evidence-handling obligations survive termination indefinitely.

──────────────────────────────────────────────────

9. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

10. SIGNATURES

By signing below, the parties acknowledge and agree to the terms of this Surveillance Services Agreement.

INVESTIGATOR / AGENCY:
Name: {{company_name}}
License No.: ___________________________
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },

  /* ───────── 3. Background Investigation Agreement ───────── */
  {
    name: "Background Investigation Agreement",
    description:
      "Contract for background checks, due diligence, and records research — includes FCRA compliance provisions, permissible purpose requirements, and data handling.",
    content: `BACKGROUND INVESTIGATION SERVICES AGREEMENT

This Background Investigation Services Agreement ("Agreement") is entered into as of {{date}} by and between:

INVESTIGATOR / AGENCY:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Investigator")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SCOPE OF BACKGROUND INVESTIGATION

The Investigator shall conduct background investigation services as described below:

{{project_description}}

Specific Services:
{{scope_of_work}}

The investigation may include, as applicable:
• Criminal history records search (federal, state, and county levels)
• Civil litigation and judgment records
• Bankruptcy and tax lien searches
• Employment and education verification
• Professional license verification
• Credit history review (with proper authorization and permissible purpose)
• Social media and digital presence analysis
• Reference interviews
• Address history and identity verification
• Corporate records and business filings
• Asset searches and financial profile development

──────────────────────────────────────────────────

2. FAIR CREDIT REPORTING ACT (FCRA) COMPLIANCE

A. If any portion of this investigation constitutes a "consumer report" under the Fair Credit Reporting Act (15 U.S.C. § 1681 et seq.), the Client certifies that:
• The Client has a permissible purpose under FCRA § 1681b for requesting the report
• The Client has provided all required disclosures and obtained all necessary authorizations from the subject
• The Client will comply with all adverse action requirements under FCRA §§ 1681b and 1681m

B. The Client acknowledges that certain information (e.g., arrest records older than seven years, sealed or expunged records) may be restricted from disclosure under applicable state and federal law.

C. The Investigator shall not furnish information known to be inaccurate, and shall maintain reasonable procedures to ensure accuracy in accordance with FCRA § 1681e.

──────────────────────────────────────────────────

3. DATA PRIVACY AND SECURITY

A. All personally identifiable information (PII) collected during the investigation shall be handled in accordance with applicable data protection laws.

B. The Investigator shall:
• Store all records in encrypted, access-controlled systems
• Limit access to authorized personnel only
• Dispose of records securely upon the Client's written request or after the retention period
• Notify the Client promptly in the event of any data breach affecting the investigation records

C. Record Retention: Investigation records shall be retained for three (3) years from the date of the final report, unless a longer period is required by law or requested in writing by the Client.

──────────────────────────────────────────────────

4. COMPENSATION

Estimated Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Background investigation services are billed on a per-search or flat-fee basis as set forth in the attached fee schedule. Database access fees, court record retrieval fees, and third-party verification costs are billed at actual cost.

──────────────────────────────────────────────────

5. REPORTING AND DELIVERABLES

A. The Investigator shall deliver a comprehensive written report containing:
• Summary of databases and sources searched
• Factual findings with source attribution
• Copies of relevant public records obtained
• Verification results with dates of confirmation
• Any discrepancies identified between reported and verified information

B. Reports shall contain only verifiable, factual information. The Investigator does not render opinions on suitability for employment, creditworthiness, or character unless expressly requested and permitted by law.

──────────────────────────────────────────────────

6. CONFIDENTIALITY

All information obtained during the background investigation is strictly confidential. The Investigator shall not disclose findings to any party other than the authorized Client representative without written consent, except as required by law or legal process.

──────────────────────────────────────────────────

7. LIMITATION OF LIABILITY AND DISCLAIMER

A. While the Investigator shall exercise reasonable diligence, background investigations are limited by the accuracy and completeness of source databases and records. The Investigator does not warrant that all records have been identified.

B. The Investigator's liability is limited to the fees paid under this Agreement.

C. The Client agrees to indemnify the Investigator from claims arising from the Client's failure to comply with FCRA or other applicable laws governing the use of background investigation results.

──────────────────────────────────────────────────

8. TERM AND TERMINATION

{{contract_length}}

This Agreement may be terminated by either party upon written notice. All fees for work completed through the date of termination shall be due and payable. Confidentiality and data protection obligations survive termination.

──────────────────────────────────────────────────

9. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

10. SIGNATURES

By signing below, the parties acknowledge that they have read, understood, and agree to be bound by this Agreement.

INVESTIGATOR / AGENCY:
Name: {{company_name}}
License No.: ___________________________
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },

  /* ───────── 4. Insurance Fraud Investigation Agreement ───────── */
  {
    name: "Insurance Fraud Investigation Agreement",
    description:
      "Specialized agreement for insurance fraud, workers' compensation, and claims investigation — covers SIU coordination, statement collection, and regulatory compliance.",
    content: `INSURANCE FRAUD INVESTIGATION AGREEMENT

This Insurance Fraud Investigation Agreement ("Agreement") is entered into as of {{date}} by and between:

INVESTIGATOR / AGENCY:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Investigator")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. PURPOSE

The Client retains the Investigator to conduct an investigation into a suspected fraudulent insurance claim or workers' compensation matter as described below:

Claim / Case Description:
{{project_description}}

Investigation Objectives:
{{scope_of_work}}

──────────────────────────────────────────────────

2. SCOPE OF INVESTIGATIVE SERVICES

The Investigator may employ the following methods as warranted by the circumstances:
• Surveillance of the claimant (stationary and mobile)
• Activity checks and canvassing
• Recorded and/or sworn statements from the claimant, witnesses, and involved parties
• Scene inspection and documentation
• Medical records review and analysis (with proper authorizations)
• Social media investigation and open-source intelligence
• Sub Rosa video documentation
• Background investigation of the claimant
• Coordination with the Client's Special Investigations Unit (SIU)
• Expert consultation (accident reconstruction, medical, vocational)

──────────────────────────────────────────────────

3. REGULATORY COMPLIANCE

A. All investigative activities shall comply with:
• State Department of Insurance regulations
• Workers' Compensation Board rules and procedures
• Health Insurance Portability and Accountability Act (HIPAA), where applicable
• State privacy and surveillance statutes
• All applicable licensing requirements for private investigators

B. The Investigator shall not engage in pretexting or misrepresentation prohibited by the Gramm-Leach-Bliley Act (GLBA) or applicable state law.

C. The Investigator shall cooperate with law enforcement referrals as directed by the Client and in accordance with state insurance fraud reporting statutes.

──────────────────────────────────────────────────

4. REPORTING AND EVIDENCE

A. Investigation Reports shall include:
• Detailed chronological narrative of investigative activities
• Surveillance logs with dates, times, locations, and observations
• Photographs and video with time/date stamps
• Recorded statement transcripts
• Medical and employment record summaries
• Index of all evidence obtained

B. All evidence shall be preserved in accordance with chain-of-custody procedures and shall be maintained in a format suitable for legal proceedings.

C. The Investigator shall be available to provide testimony at hearings, depositions, arbitrations, or trials as needed (billed at the applicable court testimony rate).

──────────────────────────────────────────────────

5. COMPENSATION

Estimated Total Cost: \${{estimated_cost}}
Estimated Hours: {{estimated_hours}}
Payment Terms: {{payment_terms}}

Surveillance deployments require a minimum of four (4) hours per day. Rush assignments and after-hours work may be subject to premium rates as set forth in the fee schedule.

──────────────────────────────────────────────────

6. CONFIDENTIALITY

A. The investigation and all related information are strictly confidential. The Investigator shall not disclose any findings except to authorized representatives of the Client.

B. If the matter is referred to law enforcement or a regulatory body, disclosure shall be made only at the Client's written direction or as required by law.

──────────────────────────────────────────────────

7. LIABILITY

A. The Investigator carries professional liability insurance with minimum coverage of $1,000,000 per occurrence and will provide a certificate of insurance upon request.

B. The Investigator makes no guarantees regarding the outcome of the investigation or the determination of fraud.

C. The Investigator's total liability shall not exceed the total fees paid under this Agreement.

──────────────────────────────────────────────────

8. TERM AND TERMINATION

{{contract_length}}

Either party may terminate this Agreement with written notice. Fees and expenses incurred through the date of termination shall be due and payable. Confidentiality obligations survive termination.

──────────────────────────────────────────────────

9. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

10. SIGNATURES

INVESTIGATOR / AGENCY:
Name: {{company_name}}
License No.: ___________________________
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },

  /* ───────── 5. Investigation NDA / Confidentiality Agreement ───────── */
  {
    name: "Investigation Confidentiality & Non-Disclosure Agreement",
    description:
      "Standalone NDA for sensitive investigations — covers confidential information definitions, non-disclosure obligations, remedies for breach, and survival terms.",
    content: `CONFIDENTIALITY AND NON-DISCLOSURE AGREEMENT
FOR PRIVATE INVESTIGATION SERVICES

This Confidentiality and Non-Disclosure Agreement ("NDA") is entered into as of {{date}} by and between:

INVESTIGATOR / AGENCY:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Investigator")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

(collectively, the "Parties")

──────────────────────────────────────────────────

1. PURPOSE

This NDA is executed in connection with the engagement of investigative services as described in the underlying Investigation Agreement or Retainer Agreement between the Parties. The purpose of this NDA is to protect confidential and sensitive information exchanged between the Parties.

{{project_description}}

──────────────────────────────────────────────────

2. DEFINITION OF CONFIDENTIAL INFORMATION

"Confidential Information" shall include, without limitation:
• The identity of the Client and the existence of the investigation
• The identity and personal information of investigation subjects
• All information, documents, and records provided by the Client
• All investigative findings, evidence, reports, and work product
• Surveillance locations, methods, and techniques employed
• Financial information, fee arrangements, and billing records
• Legal strategies and attorney-client communications (where applicable)
• Any information designated as confidential by either party

Confidential Information does NOT include information that:
• Is or becomes publicly available through no fault of the receiving party
• Was previously known to the receiving party without restriction
• Is independently developed without use of Confidential Information
• Is required to be disclosed by law, regulation, or court order (with prompt notice to the disclosing party)

──────────────────────────────────────────────────

3. NON-DISCLOSURE OBLIGATIONS

A. Both Parties agree to:
• Maintain the strictest confidence regarding all Confidential Information
• Use Confidential Information solely for the purpose of the investigation
• Not disclose Confidential Information to any third party without prior written consent
• Limit internal access to Confidential Information to those with a need to know
• Implement reasonable security measures to protect Confidential Information

B. The Investigator additionally agrees to:
• Not discuss the case with any person not authorized by the Client
• Not use the Client's name or case details in marketing or promotional materials
• Ensure that all employees, subcontractors, or agents with access to the information are bound by comparable confidentiality obligations

──────────────────────────────────────────────────

4. RETURN AND DESTRUCTION OF MATERIALS

Upon termination of the investigation or upon written request by the disclosing party:
• All Confidential Information in tangible form shall be returned or securely destroyed
• All electronic copies shall be permanently deleted
• A written certification of destruction shall be provided upon request

Exceptions: The Investigator may retain one archival copy as required by professional licensing regulations or as necessary for legal defense, subject to continued confidentiality obligations.

──────────────────────────────────────────────────

5. REMEDIES FOR BREACH

A. Both Parties acknowledge that a breach of this NDA may cause irreparable harm for which monetary damages would be inadequate.

B. In the event of a breach or threatened breach, the non-breaching party shall be entitled to seek injunctive relief in addition to any other remedies available at law or in equity.

C. The breaching party shall be liable for all costs, expenses, and attorneys' fees incurred by the non-breaching party in enforcing this NDA.

──────────────────────────────────────────────────

6. TERM AND SURVIVAL

A. This NDA shall remain in effect during the pendency of the investigation and for a period of five (5) years following the conclusion or termination of the engagement.

B. With respect to information that constitutes a trade secret under applicable law, the obligations of confidentiality shall survive indefinitely.

──────────────────────────────────────────────────

7. GENERAL PROVISIONS

A. Governing Law: This NDA shall be governed by the laws of the state in which the Investigator is licensed.

B. Entire Agreement: This NDA constitutes the entire agreement between the Parties with respect to confidentiality and supersedes all prior agreements on this subject.

C. Amendment: This NDA may only be amended by a written instrument signed by both Parties.

D. Severability: If any provision is found unenforceable, the remaining provisions shall continue in full force and effect.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

By signing below, the Parties acknowledge that they have read, understood, and agree to be bound by this Confidentiality and Non-Disclosure Agreement.

INVESTIGATOR / AGENCY:
Name: {{company_name}}
License No.: ___________________________
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];

export const PI_PRICING_MODELS: Record<string, unknown> = {
  HOURLY_INVESTIGATION: "hourly",
  FLAT_FEE_BACKGROUND: "flat_fee",
  SURVEILLANCE_DAILY: "daily",
  RETAINER_PLUS_HOURLY: "retainer_hourly",
};

export const PI_TERMINOLOGY = {
  proposalLabel: "Investigation Proposal",
  contractLabel: "Retainer Agreement",
  staffLabel: "Investigators",
  rateLabel: "Retainer / Hourly Rate",
};
